import {DateTime} from 'luxon';
import {Pagination, ResponseMetaData} from '../../modules/api/apiTypes';
import {Dispatch, MouseEvent, ReactNode, SetStateAction} from 'react';
import {TableSelectionType, TableSortDirection} from './CLTable.enums';
import {AppRoute} from '../../modules/routing/routing.types';
import {BorderType} from 'xlsx-js-style';
import {AutomationProps} from 'common/src/modules/automation/automation.types';
import {AIFilteredIndicatorProps} from './tableVariations/CLTableLocal/components/AIFilteredIndicator/AIFilteredIndicator';

type RowIdProvider<DataType extends object, RowId extends string | number> = DataType extends {
  id: RowId;
}
  ? {getRowId?(row: DataType): RowId}
  : {getRowId(row: DataType): RowId};

type TableSingleSelectionProps<RowId extends string | number> = {
  selection?: TableSelectionType.Single;
  selectedRow: RowId;
  onSelectionChange: Dispatch<RowId> | Dispatch<SetStateAction<RowId>>;
  rowMultiSelectWithClick?: false;

  onRowClick?(rowId: RowId, event?: MouseEvent): void;
};

type TableMultiSelectionProps<RowId extends string | number> = {
  selection: TableSelectionType.Multiple;
  selectedRows: RowId[];
  onSelectionChange: Dispatch<RowId[]> | Dispatch<SetStateAction<RowId[]>>;
  /**
   * Set to true to allow multiple rows to be selected with clicks.
   * For example, if you click to select one row and then click to
   * select another row, the first row will stay selected as well.
   */
  rowMultiSelectWithClick?: boolean;

  onRowClick?(rowId: RowId, event?: MouseEvent): void;
};

type TableSelectionProps<RowId extends string | number> =
  | TableSingleSelectionProps<RowId>
  | TableMultiSelectionProps<RowId>
  | {
      rowMultiSelectWithClick?: false;
      onRowClick?(rowId: RowId, event?: MouseEvent): void;
    };

type TableSearchProps = {
  multiSearchDelimiter?: string;
};

interface CLTablePropsBase<DataType extends object> extends AutomationProps {
  columns: CLColDef<DataType>[];
  tableId: string;
  suppressAutoHeight?: boolean;
  classNames?: CLTableTemplate;
  topPanelLeftActions?: ReactNode;
  topPanelRightActions?: ReactNode;
  useUrlFilters?: boolean;
  contextMenuActions?:
    | ContextMenuAction[]
    | ((rowItem: DataType, colId: string) => ContextMenuAction[]);
  suppressSearch?: boolean;
  suppressSettings?: boolean;
  suppressTopPanel?: boolean;
  suppressCheckboxSelection?: boolean;
  suppressHeaderCheckboxSelection?: boolean;
  placeholder?: string;
  groupSelectsChildren?: boolean;
  getRowClassName?: (data: DataType) => string;
  suppressPagination?: boolean;
  onCellDragEnd?: (data: CLTableCellDragEnd<DataType>) => void;
  suppressReorder?: boolean;
  totals?: CLTableTotals<DataType>;
  initialGroupingColumnId?: string;
  maxRowHeight?: number;
  countSelection?: boolean;
  isCheckboxSelectionDisabled?: boolean;
  checkboxTooltipText?: string;
  additionalFilters?: AdditionalFilter[];

  onColumnVisibilityChange?(colId: string, isVisible: boolean): void;
  onRowHover?(isHovered: boolean, rowId: string | number, event: MouseEvent): void;
}

interface CLLocalTablePropsBase<DataType extends object> extends CLTablePropsBase<DataType> {
  data: DataType[];
  paginationSize?: number;
  treeDataColumn?: string;
  getTreeDataPath?: (value: string) => string[];

  aiFilterData?: AIFilteredIndicatorProps;
  onRefresh?(): Promise<unknown>;
  onFilteredDataChange?(data: DataType[]): void;
  onPageChange?: (
    paginatedData: DataType[] | TableTreeData<DataType> | {groupKey: string; items: DataType[]}[],
    page: number,
  ) => void;
}

type CLLocalTableProps<
  DataType extends object,
  RowId extends string | number,
> = CLLocalTablePropsBase<DataType> &
  RowIdProvider<DataType, RowId> &
  TableSelectionProps<RowId> &
  TableSearchProps;

interface TableDataFetchParams {
  pageNumber: number;
  searchQuery: string;
  orderByColumn: string;
  orderByDirection: TableSortDirection;
}

interface CLRemoteTablePropsBase<DataType extends object> extends CLTablePropsBase<DataType> {
  fetchData: (params: TableDataFetchParams) => Promise<RemoteTableResponseData<DataType>>;
  suppressDynamicColumns?: boolean;
}

type CLRemoteTableProps<
  DataType extends object,
  RowId extends string | number,
> = CLRemoteTablePropsBase<DataType> & RowIdProvider<DataType, RowId> & TableSelectionProps<RowId>;

type CLTableProps<DataType extends object, RowId extends string | number> =
  | CLLocalTableProps<DataType, RowId>
  | CLRemoteTableProps<DataType, RowId>;

interface RemoteTableResponseData<DataType extends object> {
  items: DataType[];
  meta: ResponseMetaData;
  pagination: Pagination;
}

type AllowedColValue = string | string[] | number | number[] | DateTime | Date;

// return keyof object which value extends provided Value
type ValueTypeKeys<Obj extends object, Value> = {
  [Property in keyof Obj]: Property extends symbol
    ? never
    : Property extends number
      ? never
      : Obj[Property] extends Value
        ? Property
        : never;
}[keyof Obj];

type LinkValueGetter<DataType, Value> = Value | ((data: DataType) => Value);

interface QuickLinkBase<DataType> {
  icon: LinkValueGetter<DataType, string>;
  content: LinkValueGetter<DataType, ReactNode>;
  isDisabled?: LinkValueGetter<DataType, boolean>;
  isHidden?: LinkValueGetter<DataType, boolean>;
}

type QuickLinkAction<DataType> =
  | {onClick: (params: DataType, event: MouseEvent) => void}
  | {link: AppRoute; openInNewTab?: LinkValueGetter<DataType, boolean>};

type QuickLink<DataType> = QuickLinkBase<DataType> & QuickLinkAction<DataType>;

interface ColSimpleValue<DataType extends object> {
  field: ValueTypeKeys<DataType, AllowedColValue>;
}

interface ColValueGetter<DataType extends object> {
  field: string;
  valueGetter: (data: DataType) => AllowedColValue;
}

type ColValueProvider<DataType extends object> =
  | ColSimpleValue<DataType>
  | ColValueGetter<DataType>;

interface GroupRowRendererProps<DataType extends object> {
  groupKey: string;
  groupItems: DataType[];
  metadata: CLTableMetaData;
}

type CLColDef<DataType extends object> = {
  id?: string;
  headerName: string;
  headerComponent?: ReactNode;
  sort?: TableSortDirection;
  searchable?: boolean;
  filterable?: boolean;
  sortable?: boolean;
  groupable?: boolean;
  isVirtual?: boolean;
  searchValueGetter?: (data: DataType) => AllowedColValue;
  filterValueGetter?: (data: DataType) => AllowedColValue;
  sortValueGetter?: (data: DataType) => AllowedColValue;
  isDefaultHidden?: boolean;
  minWidth?: number;
  maxWidth?: number;
  width?: number;
  renderer?: (data: {value: AllowedColValue; item: DataType}) => ReactNode;
  quickLinks?: QuickLink<DataType>[];
  comparator?: (item1: AllowedColValue, item2: AllowedColValue) => number;
  filterFunc?: (data: AllowedColValue, appliedFilters: string[]) => boolean;
  searchFunc?: (data: AllowedColValue, searchTerm: string) => boolean;
  headerTooltip?: ReactNode;
  cellClassName?: string;
  isCellDraggable?: boolean;
  getFilterPath?: (filter: string) => string[];
  fullWidth?: boolean;
  fullHeight?: boolean;
  groupRowRenderer?: (props: GroupRowRendererProps<DataType>) => ReactNode;
  clipboardValueGetter?: (data: DataType) => AllowedColValue;
} & ColValueProvider<DataType>;

interface CLColState {
  id: string;
  isHidden: boolean;
  width: number;
  order: number;
  originalOrder: number;
}

type CLColDefWithState<DataType extends object> = CLColDef<DataType> & CLColState;

interface CLTableState {
  sort: string;
  sortDirection: TableSortDirection;
  columnsState: CLColState[];
  groupingColumnId?: string;
}

interface CLTableCellDrag {
  isDragging: boolean;
  cursorPosition: {
    x: number;
    y: number;
  };
  draggedCellRowId: string;
  draggedOverRowId: string;
}

interface ContextMenuAction {
  text: string;
  disabled?: boolean | ((colDefs: CLColDef<object>) => boolean);

  onClick(
    cellValue: AllowedColValue,
    rowId: string,
    colDefs: CLColDef<object>,
    data?: object,
  ): void;
}

interface CLTableTemplate {
  table?: string;
  header?: string;
  hr?: string;
  row?: string;
  rowActive?: string;
  rowGrouped?: string;
  cell?: string;
  cellContent?: string;
  footer?: string;
  checkbox?: string;
  checkboxCell?: string;
  topPanel?: string;
  searchInput?: string;
  groupRow?: string;
  totalLabel?: string;
}

type TableTreeData<DataType extends object> = Array<{
  item: DataType & {treeKey: string; treeKeyArray: string[]};
  children: TableTreeData<DataType>;
}>;

interface CLProcessCellParams<DataType extends object> {
  value: AllowedColValue;
  column: CLColDef<DataType>;
  rowData: DataType;
}

interface CLProcessHeaderParams<DataType extends object> {
  column: CLColDef<DataType>;
}

interface CLTableExportToExcel<DataType extends object> {
  filenamePrefix: string;
  allColumns?: boolean;
  onlyVisibleColumns?: boolean;
  onlySelected?: boolean;
  /** Excel column filtering. */
  enableAutoFilter?: boolean;
  styles?: {
    /**
     * RGB background color for header cell.
     * @default fcfc05
     */
    headerBackgroundColor?: string;
    /**
     * RGB color for header cell cell borders.
     * @default 000000
     */
    cellBorderColor?: string;
    /**
     * RGB color for header cell cell borders.
     * @default thin
     */
    cellBorderType?: BorderType;
    /**
     * RGB color for header cell cell borders.
     * @default thin
     */
    cellBackgroundColor?: BorderType;
    wrappedText?: boolean;
  };
  columns?: string[] | (CLColDef<DataType> & {isLink?: boolean})[];

  /** Custom cells formatting. */
  processCellCallback?(params: CLProcessCellParams<DataType>): string;

  /** Custom headers formatting. */
  processHeaderCallback?(params: CLProcessHeaderParams<DataType>): string;
}

interface CLTableApi<DataType extends object> {
  exportDataAsExcel: (params: CLTableExportToExcel<DataType>) => void;
  setRowData?: (cb: (prevState: DataType[]) => DataType[]) => void;
  rowData: DataType[];
  tableColumns: CLColDefWithState<DataType>[];
  tableData?: DataType[];
}

interface CLTableRemoteApi<DataType extends object> extends CLTableApi<DataType> {
  //Since remote table is in charge of loading the data, enable hard reloading of the data when needed
  refreshData: () => void;
}

interface CLTableCellDragEnd<DataType extends object> {
  data?: DataType[];
  draggedCellRowId: string;
  draggedOverRowId: string;
}

type CLTableTotals<DataType extends object> = {
  data: (string | CLTableTotalsConfig<DataType>)[];
  tooltipText?: string;
};

type CLTableTotalsConfig<DataType extends object> =
  | {
      colId: string;
      getter?: ValueTypeKeys<DataType, number> | ((data: DataType) => number);
      isPercentage?: false;
    }
  | {
      colId: string;
      valueGetter: ValueTypeKeys<DataType, number> | ((data: DataType) => number);
      totalGetter: ValueTypeKeys<DataType, number> | ((data: DataType) => number);
      isPercentage: true;
    }
  | {
      colId: string;
      value: string | number;
    };

interface CLTableMetaData {
  columns: CLColState[];
}

enum CLTableSearchParamKey {
  Filter = 'q',
}

interface DynamicColumnItem {
  id: string;
  title?: string;
}

type AdditionalFilter = {
  id: string;
  checked: boolean;
  label: string;
  onChange: (isChecked: boolean) => void;
  disabled?: boolean;
};

export {
  CLTableProps,
  CLTableState,
  CLColDef,
  CLColState,
  CLColDefWithState,
  CLLocalTableProps,
  CLRemoteTableProps,
  AllowedColValue,
  QuickLink,
  LinkValueGetter,
  ContextMenuAction,
  CLTableTemplate,
  ValueTypeKeys,
  ColSimpleValue,
  ColValueGetter,
  ColValueProvider,
  TableSelectionProps,
  TableDataFetchParams,
  RemoteTableResponseData,
  TableTreeData,
  CLTableApi,
  CLTableRemoteApi,
  CLTableExportToExcel,
  CLProcessHeaderParams,
  CLProcessCellParams,
  CLTableCellDragEnd,
  CLTableCellDrag,
  CLTableTotals,
  CLTableMetaData,
  GroupRowRendererProps,
  CLTableSearchParamKey,
  DynamicColumnItem,
  AdditionalFilter,
};
